import 'smoothie/assets/reset.css'; // A web reset. On native, all global stylesheets are automatically ignored. CSS is enabled via metro.config.js.

import { decode } from 'base-64';
import * as ExpoAudio from 'expo-audio';
import { Slot } from 'expo-router';
import Head from 'expo-router/head';
import * as SplashScreen from 'expo-splash-screen';
import * as React from 'react';
import { Platform, Text } from 'react-native';
import { ClickOutsideProvider } from 'react-native-click-outside';
import ErrorBoundary from 'react-native-error-boundary';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { isWeb } from 'smoothie';

import AuthProvider from '../auth/providers/AuthProvider';
import MaintenanceBanner from '../common/components/MaintenanceBanner';
import { DesignSystemProvider } from '../common/providers/DesignSystemProvider';
import StorageFacade from '../common/providers/StorageFacade';
import { StorageProvider } from '../common/providers/StorageProvider';
import { StorageRecoveryStatus } from '../common/types/Storage';
import UncaughtError from '../common/UncaughtError';
import { APPLE_APP_ID } from '../config/config';
import {
    IS_LOCAL,
    IS_PRODUCTION,
    IS_STAGING,
} from '../config/FruitfulEnvironment';
import CustomerIOFacade from '../events/customerio/CustomerIOFacade';
import MixpanelFacade from '../events/mixpanel/MixpanelFacade';
import DataFetchingProvider from '../http/providers/DataFetchingProvider';
import DatadogFacade, {
    DatadogProvider,
} from '../logger/datadog/DatadogFacade';
import LogRocketFacade from '../logger/log-rocket/LogRocketFacade';
import { captureWarning, handleUncaughtException } from '../logger/Logger';
import SentryFacade from '../logger/sentry/SentryFacade';
import CheckNetworkConnectivity from '../middleware/components/CheckNetworkConnectivity';
import CheckRequiredUpdate from '../middleware/components/CheckRequiredUpdate';
import RegisterRoutingInstrumentation from '../middleware/components/RegisterRoutingInstrumentation';
import PushNotificationsListenerProvider from '../notifications/providers/PushNotificationsListenerProvider';
import SessionManager from '../session/components/SessionManager';
import UserProvider from '../user/providers/UserProvider';

if (IS_LOCAL) {
    // Adds additional error handling: https://docs.expo.dev/develop/development-builds/use-development-builds/#add-error-handling
    // eslint-disable-next-line @typescript-eslint/no-require-imports -- intentional side-effect import for dev client
    require('expo-dev-client');
}

SplashScreen.setOptions({
    duration: 1000,
    fade: true,
});

SentryFacade.init();
LogRocketFacade.init();
DatadogFacade.init();
MixpanelFacade.init();
void CustomerIOFacade.init();

const storageRecoveryStatus = StorageFacade.getRecoveryStatus();
if (storageRecoveryStatus !== StorageRecoveryStatus.NOT_NEEDED) {
    captureWarning(
        storageRecoveryStatus === StorageRecoveryStatus.SUCCEEDED
            ? 'Recovered from an unreadable encrypted local storage instance'
            : 'Failed to recover from an unreadable encrypted local storage instance',
    );
}

// Quiltt requires atob to function in react native
// eslint-disable-next-line @typescript-eslint/strict-boolean-expressions,@typescript-eslint/no-unsafe-assignment
if (!global.atob) global.atob = decode;

// Disabling font scaling for now until design can account for it
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-explicit-any
if ((Text as any).defaultProps == null) (Text as any).defaultProps = {};
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-explicit-any
(Text as any).defaultProps.allowFontScaling = false;

if (Platform.OS === 'ios') {
    void ExpoAudio.setAudioModeAsync({
        playsInSilentMode: true,
        interruptionMode: 'doNotMix',
        allowsRecording: false,
        shouldPlayInBackground: false,
        shouldRouteThroughEarpiece: true,
        interruptionModeAndroid: 'doNotMix',
    });
}

// This is our root layout, and is required to handle the auth-related redirects we do in the app layout
// All providers need to be added here
// <Slot /> is where expo-router mounts its navigator and our app
// (app)/_layout.tsx is the top level middleware
function RootLayout() {
    return (
        <>
            {Platform.OS === 'web' && (
                <Head>
                    {/* Apple Smart Banner: https://docs.expo.dev/guides/deep-linking/#apple-smart-banner */}
                    {(IS_STAGING || IS_PRODUCTION) && (
                        <meta
                            name="apple-itunes-app"
                            content={`app-id=${APPLE_APP_ID}`}
                        />
                    )}
                </Head>
            )}

            <RegisterRoutingInstrumentation>
                <CheckRequiredUpdate>
                    <DatadogProvider>
                        <StorageProvider>
                            <DataFetchingProvider>
                                <DesignSystemProvider>
                                    <ErrorBoundary
                                        onError={handleUncaughtException}
                                        FallbackComponent={UncaughtError}
                                    >
                                        <CheckNetworkConnectivity>
                                            <PushNotificationsListenerProvider>
                                                <AuthProvider>
                                                    <SessionManager>
                                                        <UserProvider>
                                                            <SafeAreaProvider>
                                                                <ClickOutsideProvider>
                                                                    <MaintenanceBanner />
                                                                    <Slot />
                                                                </ClickOutsideProvider>
                                                            </SafeAreaProvider>
                                                        </UserProvider>
                                                    </SessionManager>
                                                </AuthProvider>
                                            </PushNotificationsListenerProvider>
                                        </CheckNetworkConnectivity>
                                    </ErrorBoundary>
                                </DesignSystemProvider>
                            </DataFetchingProvider>
                        </StorageProvider>
                    </DatadogProvider>
                </CheckRequiredUpdate>
            </RegisterRoutingInstrumentation>
        </>
    );
}

// Sentry.wrap: Instrument the app with touch event tracking and automatic performance monitoring.
export default SentryFacade.isEnabled() && !isWeb
    ? SentryFacade.Sentry.wrap(RootLayout)
    : RootLayout;
